import type { DeleteTaxCodeMutationOptions } from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { UUID } from '~/types/common';

import { queryKeysTaxCode } from '../queryKeys';

import { deleteTaxCode } from './deleteTaxCode';

/**
 * Deletes one tax code and refreshes the remaining tax-code views.
 *
 * @param options - Additional mutation options.
 * @returns The tax-code deletion mutation.
 * @see https://app.dev.vestigas.com/redoc#tag/Tax-Codes
 */
export const useMutationDeleteTaxCode = (
  options?: DeleteTaxCodeMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taxCodeId: UUID) => deleteTaxCode(taxCodeId),
    ...options,
    onSuccess(data, taxCodeId, onMutateResult, context) {
      queryClient.removeQueries({
        exact: true,
        queryKey: queryKeysTaxCode.get(taxCodeId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeysTaxCode.getAll(),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeysTaxCode.filterOptions(),
      });

      return options?.onSuccess?.(data, taxCodeId, onMutateResult, context);
    },
  });
};
